"use client";

import { useState, useEffect } from "react";
import { useProfileDetails } from "@/hooks/useProfileDetails";
import { useServiceTags } from "@/hooks/useServiceTags";
import ExpertProfile from "./ExpertProfile";
import { ProfileDetailsProps } from "@/types/ProfileDetails";
import { ordersApi, authApi } from "@/lib/api";
import { AeraButton } from "@/app/components/ui/AeraButton";

export default function ProfileDetails(props: ProfileDetailsProps) {
  const {
    bestFor,
    worksWith,
    whatWorkerDoes,
    builtByExpert,
    editable = false,
    onBestForChange,
    onWhatWorkerDoesChange,
    onBuiltByExpertChange,
  } = props;

  // Fetch service tags from API
  const { tagData, loading: tagsLoading } = useServiceTags(props.workerData?.id);

  // Use API data if available, otherwise fall back to props
  const displayBestFor = tagsLoading ? [] : (tagData.bestFor.length > 0 ? tagData.bestFor : bestFor.items);
  const displayWorksWith = tagsLoading ? [] : (tagData.integrations.length > 0 ? tagData.integrations : worksWith);
  const displayWhatWorkerDoes = tagsLoading ? [] : (tagData.workerFunctions.length > 0 ? tagData.workerFunctions : whatWorkerDoes);

  // Reviews state
  const [hasPurchased, setHasPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(true);
  const [userOrderId, setUserOrderId] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Fetch current user to check review ownership
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await authApi.getMe() as any;

        // Handle different response formats
        const user = response?.user || response;
        const userId = user?.id;

        if (userId) {
          setCurrentUserId(userId);
          setCurrentUser(user);

        } else {
          console.warn('⚠️ No user ID found in response:', response);
          setCurrentUserId(null);
          setCurrentUser(null);
        }
      } catch (error) {
        console.error("❌ Failed to fetch current user:", error);
        // User not logged in
        setCurrentUserId(null);
        setCurrentUser(null);
      }
    };

    fetchCurrentUser();
  }, []);

  // Check if user has purchased this service
  useEffect(() => {
    const checkPurchase = async () => {
      if (!props.workerData?.id) {
        setCheckingPurchase(false);
        return;
      }

      try {
        setCheckingPurchase(true);
        const response = await ordersApi.list({ role: 'buyer' }) as { orders: any[] };

        if (response?.orders && Array.isArray(response.orders)) {
          // Find ALL completed orders for this service (not just the first one)
          const userOrders = response.orders.filter(
            (order: any) => {

              return order.serviceId === props.workerData?.id && order.status === 'completed';
            }
          );

          if (userOrders.length > 0) {

            setHasPurchased(true);
            // Store the FIRST order ID for creating new reviews
            setUserOrderId(userOrders[0].id);

            // Store ALL order IDs to check review ownership
            // We'll use this to check if user has already reviewed via ANY of their orders
            const allOrderIds = userOrders.map((o: any) => o.id);


            // Store in state for later use
            (window as any).__userOrderIds = allOrderIds;
          } else {

            setHasPurchased(false);
            setUserOrderId(null);
            (window as any).__userOrderIds = [];
          }
        }
      } catch (error) {
        console.error("Failed to check purchase status:", error);
        // If error (e.g., not logged in), assume not purchased
        setHasPurchased(false);
        setUserOrderId(null);
        (window as any).__userOrderIds = [];
      } finally {
        setCheckingPurchase(false);
      }
    };

    checkPurchase();
  }, [props.workerData?.id]);

  const {
    handleRemoveItem,
  } = useProfileDetails(props);

  return (
    <div className="p-6 overflow-y-auto h-[calc(100dvh-160px)] md:h-[calc(100dvh-200px)] xl:h-[calc(100dvh-240px)]">
      {/* Best for */}
      <div className="mb-8 grid items-center gap-2">
        <div className="grid grid-cols-[auto_auto] items-center justify-between">
          <h2 className="text-lg font-semibold text-aera-charcoal">
            {bestFor.title}
          </h2>
          <p className="text-sm text-aera-gray">{bestFor.subtitle}</p>
        </div>
        {displayBestFor.length > 0 && (
          <ul className="space-y-2">
            {displayBestFor.map((item, index) => (
              <li key={index} className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-aera-greendark shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {editable && onBestForChange ? (
                  <>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newItems = [...displayBestFor];
                        newItems[index] = e.target.value;
                        onBestForChange({ ...bestFor, items: newItems });
                      }}
                      className="flex-1 text-sm text-aera-charcoal placeholder:text-aera-gray bg-transparent border-b border-aera-lightgray focus:border-aera-charcoal focus:outline-none px-1"
                    />
                    <AeraButton
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        const newItems = displayBestFor.filter((_, i) => i !== index);
                        onBestForChange({ ...bestFor, items: newItems });
                      }}
                      className="p-0! text-aera-slate hover:text-aera-red"
                      icon={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      }
                    />
                  </>
                ) : (
                  <span className="text-sm text-aera-slate">{item}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Works with */}
      <div className="mb-8 grid items-center gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-aera-charcoal">
            {/* Works with: */}
            ใช้ร่วมกับ:
          </h2>
          {displayWorksWith.map((item, index) => (
            <div
              key={index}
              className="px-2 py-1 border border-aera-lightgray/80 rounded-lg text-sm text-aera-slate flex items-center gap-2 bg-aera-white"
            >
              <span>{item}</span>
              {editable && (
                <AeraButton
                  variant="ghost"
                  size="xs"
                  onClick={() => handleRemoveItem(index)}
                  className="p-0! text-aera-slate hover:text-aera-red"
                  aria-label="Remove item"
                  icon={
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  }
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* What this worker does */}
      <div className="mb-8 grid items-center gap-2">
        <h2 className="text-lg font-semibold text-aera-charcoal">
          {/* What this worker does */}
          สิ่งที่ AI นี้ทำได้
        </h2>
        {displayWhatWorkerDoes.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {displayWhatWorkerDoes.map((item, index) => (
              <div
                key={index}
                className="px-2 py-1 border border-aera-lightgray/80 rounded-lg text-sm text-aera-slate flex items-center gap-2"
              >
                {editable && onWhatWorkerDoesChange ? (
                  <>
                    <svg
                      className="w-5 h-5 text-aera-slate"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <input
                      type="text"
                      value={item}
                      onChange={(e) => {
                        const newItems = [...displayWhatWorkerDoes];
                        newItems[index] = e.target.value;
                        onWhatWorkerDoesChange(newItems);
                      }}
                      className="flex-1 text-aera-charcoal placeholder:text-aera-gray bg-transparent border-b border-aera-lightgray focus:border-aera-charcoal focus:outline-none px-1 min-w-[100px]"
                    />
                    <AeraButton
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        const newItems = displayWhatWorkerDoes.filter((_, i) => i !== index);
                        onWhatWorkerDoesChange(newItems);
                      }}
                      className="p-0! text-aera-slate hover:text-aera-red"
                      icon={
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      }
                    />
                  </>

                ) : (
                  <>
                    <svg
                      className="w-4 h-4 text-aera-slate"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    <span>{item}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Built by expert */}
      <div>
        <h2 className="text-lg font-semibold text-aera-slate mb-4">
          {/* Built by expert */}
          พัฒนาโดยผู้เชี่ยวชาญ
        </h2>

        <div className="flex flex-col gap-4">
          <ExpertProfile
            creator={props.workerData?.creator}
            editable={editable}
            builtByExpert={builtByExpert}
            onBuiltByExpertChange={onBuiltByExpertChange}
            onImageUpload={props.onExpertImageUpload}
          />

          <ul className="space-y-3">
            {builtByExpert.points.map((point, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="w-2 h-2 bg-aera-lightgray rounded-full mt-2 shrink-0"></div>
                {editable && onBuiltByExpertChange ? (
                  <>
                    {/* Add expert point... */}
                    <input
                      type="text"
                      value={point}
                      onChange={(e) => {
                        const newPoints = [...builtByExpert.points];
                        newPoints[index] = e.target.value;
                        onBuiltByExpertChange({ ...builtByExpert, points: newPoints });
                      }}
                      placeholder="เพิ่มจุดเด่นของผู้เชี่ยวชาญ..."
                      className="flex-1 text-sm text-aera-charcoal placeholder:text-aera-gray bg-transparent border-b border-gray-300 focus:border-gray-900 focus:outline-none px-1"
                    />
                    <AeraButton
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        const newPoints = builtByExpert.points.filter((_, i) => i !== index);
                        onBuiltByExpertChange({ ...builtByExpert, points: newPoints });
                      }}
                      className="p-0! text-aera-slate hover:text-aera-red"
                      icon={
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      }
                    />
                  </>
                ) : (
                  <span className="text-sm text-aera-slate leading-relaxed">{point}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
        {props.errors?.builtByExpert && (
          <p className="mt-2 text-xs text-aera-red">{props.errors.builtByExpert}</p>
        )}
      </div>
    </div>
  );
}

